/**
 * VariableHelp - the `$(…)` reference shown under a command's response text.
 *
 * R20 chat 3. A collapsible list of every variable a response can use — what
 * each shows, whether it works in this channel right now, and what it prints
 * instead when it can't — with click-to-insert at the textarea's cursor. Under
 * the textarea, whatever the disclosure's state, a hint names any `$(name)`
 * the text uses that is not a variable (printed as typed, R20 N7) and any it
 * uses that is degrading in this channel now.
 *
 * Data arrives through <VariableHelpProvider>, which a page mounts once:
 * Commands passes the channel manifest's `variables` (with per-channel
 * status), Admin → Commands the channel-free admin listing (status null). The
 * component never fetches; one request per page, not one per textarea.
 *
 * Four rules from the forks (R20 chat 3):
 *   - EVERY VARIABLE IS LISTED (C4). A variable whose module is off here is
 *     shown with what it prints, because a command already using it is
 *     printing that line now.
 *   - THE TEXTS ARE THE SERVER'S. Nothing here composes a degrade string;
 *     `degrades[].text` is exactly what chat gets.
 *   - INSERT REPLACES THE SELECTION and puts the caret after the insert. The
 *     textarea is controlled, so the new value goes through `onInsert` and
 *     the caret is restored after React commits it.
 *   - A DISABLED TEXTAREA DISABLES INSERTION, styled locally because `.btn`
 *     has no disabled style (IDEAS).
 *
 * @component
 * @example
 * <VariableHelpProvider value={{ variables, loadError, retry, scoped: true }}>
 *   <textarea ref={ref} value={text} onChange={…} />
 *   <VariableHelp text={text} textareaRef={ref} onInsert={setText} />
 * </VariableHelpProvider>
 */

import { createContext, useContext, useMemo, useState, type RefObject } from 'react'
import { AlertCircle, Braces, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import type { VariableDegrade, VariableInfo, VariableStatus } from '@/api/capabilitiesApi'
import { templateReferences } from '@/components/templateReferences'

export interface VariableHelpData {
  /** Null while loading, or after a failed load. */
  variables: VariableInfo[] | null
  /** A failed load's sentence (already through apiErrorMessage). */
  loadError: string | null
  /** Re-fetch after a failure. */
  retry: () => void
  /** True when the listing carries one channel's status (the Commands page). */
  scoped: boolean
}

const VariableHelpContext = createContext<VariableHelpData | null>(null)

export const VariableHelpProvider = VariableHelpContext.Provider

/** The random.pick syntax is render's own keyword, not a variable; offered as one chip. */
const RANDOM_PICK_EXAMPLE = "$(random.pick 'one' 'two')"

/** The status badge's look and words. Warning styling is composed from tokens: no .badge-warning exists. */
const STATUS_BADGE: Record<VariableStatus, { className: string; label: string; title: string }> = {
  available: {
    className: 'badge-success',
    label: 'Works here',
    title: 'Resolves in this channel right now.',
  },
  disabled: {
    className: 'badge bg-state-warning-bg text-state-warning',
    label: 'Module off here',
    title: 'Its module is switched off for this channel.',
  },
  unresolved: {
    className: 'badge bg-state-warning-bg text-state-warning',
    label: "Couldn't check",
    title: 'The database could not say whether its module is on.',
  },
  unknown: {
    className: 'badge-danger',
    label: 'Not loaded',
    title: 'Its module is not loaded on the bot.',
  },
}

/** What a variable prints for its current status, if it isn't working. */
function currentDegrade(v: VariableInfo): VariableDegrade | null {
  if (!v.status || v.status === 'available') return null
  return v.degrades.find((d) => d.reason === v.status) ?? null
}

/**
 * Collapse reasons that print the same text into one line. A merged line keeps
 * a "when" only if every member has one, joined — a module's own reason has
 * none, and borrowing a core label for it would describe the wrong condition.
 */
function groupDegrades(degrades: VariableDegrade[]): { text: string; when: string | null }[] {
  const groups = new Map<string, VariableDegrade[]>()
  for (const d of degrades) groups.set(d.text, [...(groups.get(d.text) ?? []), d])
  return [...groups.entries()].map(([text, members]) => ({
    text,
    when: members.every((m) => m.when) ? members.map((m) => m.when).join(', or ') : null,
  }))
}

export default function VariableHelp({
  text,
  textareaRef,
  onInsert,
  disabled = false,
}: {
  /** The response text as it stands, for the hint. */
  text: string
  textareaRef: RefObject<HTMLTextAreaElement>
  /** Receives the whole new text after an insert. */
  onInsert: (next: string) => void
  /** True when the textarea is disabled (locked, override off, busy). */
  disabled?: boolean
}) {
  const data = useContext(VariableHelpContext)
  const [open, setOpen] = useState(false)

  const variables = data?.variables ?? null
  const byName = useMemo(
    () => new Map((variables ?? []).map((v) => [v.name, v])),
    [variables],
  )

  // The hint needs the listing; without it every name would look unknown.
  const references = useMemo(() => [...templateReferences(text)].sort(), [text])
  const unknown = variables ? references.filter((n) => !byName.has(n)) : []
  const degrading = data?.scoped
    ? references
        .map((n) => byName.get(n))
        .filter((v): v is VariableInfo => !!v && !!currentDegrade(v))
    : []

  const insert = (token: string) => {
    const el = textareaRef.current
    if (!el || disabled) return
    const start = el.selectionStart ?? text.length
    const end = el.selectionEnd ?? text.length
    onInsert(text.slice(0, start) + token + text.slice(end))
    const caret = start + token.length
    // After React commits the new value; setting it now would be overwritten.
    requestAnimationFrame(() => {
      const node = textareaRef.current
      if (!node) return
      node.focus()
      node.setSelectionRange(caret, caret)
    })
  }

  if (!data) return null

  return (
    <div className="mt-1.5 space-y-1.5">
      {(unknown.length > 0 || degrading.length > 0) && (
        <div className="text-xs text-state-warning flex items-start gap-1.5">
          <AlertCircle size={13} className="shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            {unknown.length > 0 && (
              <p>
                {unknown.map((n) => `$(${n})`).join(', ')}{' '}
                {unknown.length === 1 ? "isn't a variable" : "aren't variables"} — printed
                exactly as typed.
              </p>
            )}
            {degrading.map((v) => (
              <p key={v.name}>
                $({v.name}) prints{' '}
                <span className="font-mono">{currentDegrade(v)?.text}</span> in this
                channel right now.
              </p>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <Braces size={13} />
        Variables
      </button>

      {open && (
        <div className="rounded border border-border bg-surface-base p-3 space-y-3">
          {variables === null ? (
            data.loadError ? (
              <div className="flex items-center gap-3 text-xs text-text-muted">
                <span>Couldn't load the variable list: {data.loadError}</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={data.retry}
                >
                  <RefreshCw size={13} className="mr-1.5" />
                  Retry
                </button>
              </div>
            ) : (
              <p className="text-xs text-text-muted">Loading variables…</p>
            )
          ) : (
            <>
              <p className="text-xs text-text-muted">
                {disabled
                  ? 'Insert is off while this field is.'
                  : 'Click a name to insert it at the cursor.'}{' '}
                {data.scoped
                  ? 'Status is for this channel, right now.'
                  : 'Global commands answer in every channel, so where a module is off they print its "not enabled" line.'}
              </p>
              <ul className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
                {variables.map((v) => (
                  <VariableRow
                    key={v.name}
                    variable={v}
                    scoped={data.scoped}
                    disabled={disabled}
                    onInsert={() => insert(`$(${v.name})`)}
                  />
                ))}
                <li className="space-y-0.5">
                  <InsertChip
                    label={RANDOM_PICK_EXAMPLE}
                    disabled={disabled}
                    onClick={() => insert(RANDOM_PICK_EXAMPLE)}
                  />
                  <p className="text-xs text-text-secondary">
                    Picks one of the quoted options at random. Options may use
                    variables; write <span className="font-mono">\'</span> for a
                    quote inside one.
                  </p>
                </li>
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function VariableRow({
  variable: v,
  scoped,
  disabled,
  onInsert,
}: {
  variable: VariableInfo
  scoped: boolean
  disabled: boolean
  onInsert: () => void
}) {
  const badge = scoped && v.status ? STATUS_BADGE[v.status] : null
  const instead = groupDegrades(v.degrades)
  return (
    <li className="space-y-0.5">
      <div className="flex items-center gap-2 flex-wrap">
        <InsertChip label={`$(${v.name})`} disabled={disabled} onClick={onInsert} />
        <span className="badge-neutral">
          {v.source === 'module' ? v.module ?? 'Module' : 'Built in'}
        </span>
        {badge && (
          <span className={badge.className} title={badge.title}>
            {badge.label}
          </span>
        )}
      </div>
      <p className="text-xs text-text-secondary">{v.description}</p>
      {instead.length > 0 && (
        <p className="text-xs text-text-muted">
          Instead may print{' '}
          {instead.map((d, i) => (
            <span key={d.text}>
              {i > 0 && ' · '}
              <span className="font-mono text-text-secondary">{d.text}</span>
              {d.when && <> {d.when}</>}
            </span>
          ))}
        </p>
      )}
    </li>
  )
}

function InsertChip({
  label,
  disabled,
  onClick,
}: {
  label: string
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? undefined : `Insert ${label} at the cursor`}
      className={clsx(
        'code-inline text-xs border border-transparent',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:border-border-focus hover:text-action-accent',
      )}
    >
      {label}
    </button>
  )
}
